import React, { Fragment, useState, useRef, useEffect } from "react";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { getAuthToken, getAuthUuid } from "../utils/service";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { currentPageAccess } from "../utils/library";

const BASE_URL = WEBSERVICE;

const getAuthHeaders = () => ({
  "Content-Type": "application/json",
  Authorization:
    "bearer " + getAuthToken(),
  UUID: getAuthUuid(),
});

const mapApiTemplate = (rec) => ({
  id: rec.id,
  name: rec.templateName,
  success: rec.successUrl || "",
  failure: rec.failureUrl || "",
  cancel: rec.cancelUrl || "",
  successMode: rec.successMode === "0" ? "url" : "hosted",
  failureMode: rec.failureMode === "0" ? "url" : "hosted",
  isDefault: rec.isDefault === 1,
  params: {
    payment_id: rec.passPaymentId === 1,
    status: rec.passStatus === 1,
    customer_email: rec.passCustomerEmail === 1,
    amount: rec.passAmount === 1,
  },
});

const isValidUrl = (url) => {
  try {
    const u = new URL(url.trim());
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const Redirects = () => {
  const merchantId = parseInt(
    localStorage.getItem(SITENAMEALIAS + "_merchant_id"),
    10
  );
  const isReadOnly = currentPageAccess() === "READ";
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Default card
  const [defaultSuccess, setDefaultSuccess] = useState("");
  const [defaultFailure, setDefaultFailure] = useState("");
  const [defaultCancel, setDefaultCancel] = useState("");
  const [defaultCardId, setDefaultCardId] = useState(null);
  const [isDefaultActive, setIsDefaultActive] = useState(true);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [tplName, setTplName] = useState("");
  const [tplSuccess, setTplSuccess] = useState("");
  const [tplFailure, setTplFailure] = useState("");
  const [tplCancel, setTplCancel] = useState("");
  const [successMode, setSuccessMode] = useState("url");
  const [failureMode, setFailureMode] = useState("url");
  const [advOpen, setAdvOpen] = useState(false);
  const [qpPaymentId, setQpPaymentId] = useState(true);
  const [qpStatus, setQpStatus] = useState(true);
  const [qpEmail, setQpEmail] = useState(true);
  const [qpAmount, setQpAmount] = useState(true);
  const [nameError, setNameError] = useState("");
  const [successUrlError, setSuccessUrlError] = useState("");
  const [failureUrlError, setFailureUrlError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Confirm
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmBody, setConfirmBody] = useState("");
  const [confirmBtnLabel, setConfirmBtnLabel] = useState("Delete");
  const [confirmBtnDanger, setConfirmBtnDanger] = useState(true);
  const confirmCb = useRef(null);

  // Toast
  const [toastMsg, setToastMsg] = useState("");
  const [toastType, setToastType] = useState("success");
  const [toastShow, setToastShow] = useState(false);
  const toastTimer = useRef(null);

  const showToast = (msg, type = "success") => {
    setToastMsg(msg);
    setToastType(type);
    setToastShow(true);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastShow(false), 3000);
  };

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/redirect-templates?merchantId=${merchantId}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const json = await res.json();
      if (json.error === "0" && Array.isArray(json.data)) {
        setTemplates(json.data.map(mapApiTemplate));
      } else {
        showToast(json.message || "Failed to load templates", "error");
      }
    } catch {
      showToast("Network error loading templates", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const loadDefaultRedirect = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/redirect-templates/default?merchantId=${merchantId}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const json = await res.json();
      if (json.error === "0" && json.data) {
        setDefaultCardId(json.data.id || null);
        setDefaultSuccess(json.data.successUrl || "");
        setDefaultFailure(json.data.failureUrl || "");
        setDefaultCancel(json.data.cancelUrl || "");
      }
    } catch {
      // silently ignore
    }
  };

  useEffect(() => {
    loadTemplates();
    loadDefaultRedirect();
  }, []);

  const openConfirm = (title, body, cb, btnLabel = "Delete", danger = true) => {
    setConfirmTitle(title);
    setConfirmBody(body);
    setConfirmBtnLabel(btnLabel);
    setConfirmBtnDanger(danger);
    confirmCb.current = cb;
    setConfirmOpen(true);
  };

  const closeConfirm = () => setConfirmOpen(false);

  // Modal helpers
  const openModal = (id) => {
    setEditingId(id);
    setNameError("");
    setSuccessUrlError("");
    setFailureUrlError("");
    setAdvOpen(false);
    if (id) {
      const t = templates.find((x) => x.id === id);
      if (t) {
        setTplName(t.name);
        setTplSuccess(t.success || "");
        setTplFailure(t.failure || "");
        setTplCancel(t.cancel || "");
        setSuccessMode(t.successMode || "url");
        setFailureMode(t.failureMode || "url");
        setQpPaymentId(t.params?.payment_id ?? true);
        setQpStatus(t.params?.status ?? true);
        setQpEmail(t.params?.customer_email ?? true);
        setQpAmount(t.params?.amount ?? true);
      }
    } else {
      setTplName("");
      setTplSuccess("");
      setTplFailure("");
      setTplCancel("");
      setSuccessMode("hosted");
      setFailureMode("hosted");
      setQpPaymentId(true);
      setQpStatus(true);
      setQpEmail(true);
      setQpAmount(true);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const saveTemplate = async (makeDefault) => {
    if (isReadOnly) {
      showToast("You need write access to perform this operation", "error");
      return;
    }
    let hasError = false;
    if (!tplName.trim()) {
      setNameError("Template name is required");
      hasError = true;
    }
    if (successMode === "url") {
      if (!tplSuccess.trim()) {
        setSuccessUrlError("Success URL is required");
        hasError = true;
      } else if (!isValidUrl(tplSuccess)) {
        setSuccessUrlError(
          "Must be a valid URL starting with http:// or https://"
        );
        hasError = true;
      } else {
        setSuccessUrlError("");
      }
    } else {
      setSuccessUrlError("");
    }
    if (failureMode === "url") {
      if (!tplFailure.trim()) {
        setFailureUrlError("Failure URL is required");
        hasError = true;
      } else if (!isValidUrl(tplFailure)) {
        setFailureUrlError(
          "Must be a valid URL starting with http:// or https://"
        );
        hasError = true;
      } else {
        setFailureUrlError("");
      }
    } else {
      setFailureUrlError("");
    }
    if (hasError) return;
    
    // Get the default URLs from the default card if they exist
    const defaultSuccessUrl = defaultSuccess || "";
    const defaultFailureUrl = defaultFailure || "";
    
    const payload = {
      id: editingId || null,
      merchantId,
      templateName: tplName.trim(),
      successUrl: successMode === "hosted" ? defaultSuccessUrl : tplSuccess.trim(),
      successMode: successMode === "hosted" ? 1 : 0,
      failureUrl: failureMode === "hosted" ? defaultFailureUrl : tplFailure.trim(),
      failureMode: failureMode === "hosted" ? 1 : 0,
      cancelUrl: "",
      passPaymentId: qpPaymentId ? 1 : 0,
      passStatus: qpStatus ? 1 : 0,
      passCustomerEmail: qpEmail ? 1 : 0,
      passAmount: qpAmount ? 1 : 0,
      isDefault: makeDefault ? 1 : 0,
    };
    setIsSaving(true);
    try {
      const res = await fetch(`${BASE_URL}/redirect-templates/create`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (json.error !== "0") {
        showToast(
          json.message ||
            (editingId
              ? "Failed to update template"
              : "Failed to create template"),
          "error"
        );
        return;
      }
      if (makeDefault) setIsDefaultActive(false);
      showToast(editingId ? "Template updated" : "Template created");
      closeModal();
      loadTemplates();
    } catch {
      showToast("Network error. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const deleteTemplate = (id) => {
    if (isReadOnly) {
      showToast("You need write access to perform this operation", "error");
      return;
    }
    openConfirm(
      "Delete Template",
      "This redirect template will be permanently removed.",
      async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/redirect-templates/${id}?merchantId=${merchantId}`,
            {
              method: "POST",
              headers: getAuthHeaders(),
            }
          );
          const json = await res.json();
          if (json.error !== "0") {
            showToast(json.message || "Failed to delete template", "error");
            return;
          }
          showToast("Template deleted");
          loadTemplates();
        } catch {
          showToast("Network error. Please try again.", "error");
        }
      }
    );
  };

  const setTemplateDefault = (id) => {
    if (isReadOnly) {
      showToast("You need write access to perform this operation", "error");
      return;
    }
    openConfirm(
      "Set as Default",
      "This template will be used as the default redirect template.",
      async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/redirect-templates/${id}/set-default?mackDefault=0&merchantId=${merchantId}`,
            {
              method: "POST",
              headers: getAuthHeaders(),
            }
          );
          const json = await res.json();
          if (json.error !== "0") {
            showToast(
              json.message || "Failed to set default template",
              "error"
            );
            return;
          }
          setIsDefaultActive(false);
          showToast("Default template updated");
          loadTemplates();
        } catch {
          showToast("Network error. Please try again.", "error");
        }
      },
      "Set Default",
      false
    );
  };

  const makeDefaultCard = () => {
    if (isReadOnly) {
      showToast("You need write access to perform this operation", "error");
      return;
    }
    if (!defaultCardId) {
      showToast("Default redirect not loaded yet", "error");
      return;
    }
    openConfirm(
      "Set as Default",
      "The default redirect card will be used as the default for all payments.",
      async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/redirect-templates/${defaultCardId}/set-default?mackDefault=1&merchantId=${merchantId}`,
            {
              method: "POST",
              headers: getAuthHeaders(),
            }
          );
          const json = await res.json();
          if (json.error !== "0") {
            showToast(json.message || "Failed to set default", "error");
            return;
          }
          setIsDefaultActive(true);
          setTemplates((prev) => prev.map((t) => ({ ...t, isDefault: false })));
          showToast("Default redirects activated");
        } catch {
          showToast("Network error. Please try again.", "error");
        }
      },
      "Set Default",
      false
    );
  };

  // Example output
  const exampleOutput = (() => {
    if (successMode === "hosted")
      return "Hosted success page (no redirect URL)";
    const base = tplSuccess || "https://yourdomain.com/success";
    const parts = [];
    if (qpPaymentId) parts.push("payment_id=pay_abc123");
    if (qpStatus) parts.push("status=succeeded");
    if (qpEmail) parts.push("customer_email=user%40email.com");
    if (qpAmount) parts.push("amount=800");
    return base + (parts.length ? "?" + parts.join("&") : "");
  })();

  return (
    <Fragment>
      <style>{`
        .rd-page{padding:32px 28px 80px}

        .rd-hdr{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:28px;gap:16px;flex-wrap:wrap}
        .rd-title{font-size:22px;font-weight:700;letter-spacing:-.4px;margin:0}
        .rd-sub{font-size:13px;opacity:.45;margin-top:5px}

        .rd-btn{height:38px;padding:0 18px;border-radius:8px;font-family:inherit;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:7px;transition:all .15s;white-space:nowrap;border:none;outline:none}
        .rd-btn-primary{background:linear-gradient(135deg,#6366f1,#8b5cf6);color:#fff;box-shadow:0 0 14px rgba(99,102,241,.3)}
        .rd-btn-primary:hover{filter:brightness(1.1)}
        .rd-btn-sec{background:var(--body-color-less);border:1px solid var(--body-color-less2);color:var(--text-color)}
        .rd-btn-sec:hover{border-color:rgba(99,102,241,.4);color:#818cf8}
        .rd-btn-ghost{background:transparent;border:1px solid var(--body-color-less2);color:var(--text-color);opacity:.6}
        .rd-btn-ghost:hover{opacity:1;color:#818cf8;border-color:rgba(99,102,241,.4)}
        .rd-btn-success{height:38px;padding:0 16px;border-radius:8px;background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.3);color:#10b981;font-family:inherit;font-size:13px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:7px;transition:all .15s;white-space:nowrap}
        .rd-btn-success:hover{background:rgba(16,185,129,.18)}
        .rd-btn-outline-ind{background:transparent;border:1.5px solid rgba(99,102,241,.3);color:#818cf8;font-family:inherit;font-size:12px;font-weight:500;cursor:pointer;display:inline-flex;align-items:center;gap:6px;padding:0 14px;border-radius:7px;height:32px;transition:all .15s;white-space:nowrap}
        .rd-btn-outline-ind:hover{background:rgba(99,102,241,.08);color:#a5b4fc}
        .rd-btn-danger-sm{background:transparent;border:1.5px solid rgba(239,68,68,.3);color:#f87171;font-family:inherit;font-size:12px;font-weight:500;cursor:pointer;padding:0 12px;border-radius:7px;height:32px;transition:all .15s;white-space:nowrap;display:inline-flex;align-items:center}
        .rd-btn-danger-sm:hover{background:rgba(239,68,68,.07);border-color:#ef4444}
        .rd-btn-cancel{height:40px;padding:0 20px;border-radius:8px;border:1px solid var(--body-color-less2);background:transparent;color:var(--text-color);opacity:.6;font-family:inherit;font-size:14px;font-weight:500;cursor:pointer;transition:all .15s}
        .rd-btn-cancel:hover{opacity:1;border-color:rgba(99,102,241,.3)}
        .rd-btn-save{height:40px;padding:0 22px;border-radius:8px;border:none;background:linear-gradient(135deg,#6366f1,#8b5cf6);color:#fff;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer;box-shadow:0 0 14px rgba(99,102,241,.3);transition:filter .15s}
        .rd-btn-save:hover{filter:brightness(1.1)}
        .rd-btn-savedef{height:40px;padding:0 22px;border-radius:8px;border:1px solid rgba(16,185,129,.4);background:rgba(16,185,129,.1);color:#10b981;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer;transition:all .15s}
        .rd-btn-savedef:hover{background:rgba(16,185,129,.18)}

        /* Card */
        .rd-card{background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:12px;padding:22px;margin-bottom:20px}
        .rd-card.rd-default-active{border-color:rgba(16,185,129,.35);box-shadow:0 0 0 1px rgba(16,185,129,.12)}
        .rd-card-title{font-size:14px;font-weight:700;margin-bottom:4px;display:flex;align-items:center;gap:9px}
        .rd-card-sub{font-size:12px;opacity:.45;margin-bottom:18px}
        .rd-default-badge{display:inline-flex;align-items:center;gap:4px;background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.25);color:#10b981;font-size:10px;font-weight:700;padding:2px 9px;border-radius:20px;letter-spacing:.3px}

        /* Field */
        .rd-field{display:flex;flex-direction:column;gap:6px;margin-bottom:16px}
        .rd-field:last-of-type{margin-bottom:0}
        .rd-label{font-size:12px;font-weight:600;opacity:.55;display:flex;align-items:center;gap:7px}
        .rd-req{color:#ef4444}
        .rd-opt{font-size:10px;font-weight:500;background:rgba(100,116,139,.12);opacity:.6;border-radius:4px;padding:1px 6px}
        .rd-input{width:100%;height:40px;background:var(--body-color);border:1.5px solid var(--body-color-less2);border-radius:8px;padding:0 14px;color:var(--text-color);font-family:inherit;font-size:14px;outline:none;transition:border-color .2s,box-shadow .2s;box-sizing:border-box}
        .rd-input::placeholder{opacity:.35}
        .rd-input:focus{border-color:rgba(99,102,241,.5);box-shadow:0 0 0 3px rgba(99,102,241,.1)}
        .rd-input-error{border-color:#ef4444 !important}
        .rd-error{font-size:11px;color:#ef4444;margin-top:2px}

        /* Table */
        .rd-tw{background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:12px;overflow:hidden;margin-bottom:20px}
        .rd-th{display:flex;align-items:center;justify-content:space-between;padding:14px 18px;border-bottom:1px solid var(--body-color-less2)}
        .rd-th-title{font-size:13px;font-weight:600}
        .rd-th-count{font-size:12px;opacity:.4}
        .rd-tbl{width:100%;border-collapse:collapse}
        .rd-tbl thead tr{border-bottom:1px solid var(--body-color-less2)}
        .rd-tbl thead th{padding:11px 16px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.7px;opacity:.35;text-align:left;white-space:nowrap}
        .rd-tbl tbody tr{border-bottom:1px solid var(--body-color-less2);transition:background .15s}
        .rd-tbl tbody tr:last-child{border-bottom:none}
        .rd-tbl tbody tr:hover{background:rgba(99,102,241,.035)}
        .rd-tbl tbody td{padding:13px 16px;font-size:13px;vertical-align:middle}
        .rd-td-name{font-weight:600}
        .rd-td-url{font-size:12px;opacity:.4;font-family:monospace;max-width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .rd-td-actions{display:flex;gap:7px;align-items:center}

        /* Empty */
        .rd-empty{display:flex;flex-direction:column;align-items:center;padding:52px 20px;gap:13px}
        .rd-empty-icon{font-size:36px;opacity:.15}
        .rd-empty-t{font-size:15px;font-weight:600;opacity:.7}
        .rd-empty-s{font-size:13px;opacity:.35}

        /* Modal */
        .rd-mov{position:fixed;inset:0;background:rgba(0,0,0,.6);backdrop-filter:blur(4px);z-index:9;display:flex;align-items:center;justify-content:center;opacity:0;pointer-events:none;transition:opacity .22s;padding:20px}
        .rd-mov.open{opacity:1;pointer-events:all}
        .rd-modal{background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:16px;width:100%;max-width:600px;max-height:90vh;overflow-y:auto;display:flex;flex-direction:column;transform:translateY(14px);transition:transform .22s ease}
        .rd-mov.open .rd-modal{transform:translateY(0)}
        .rd-modal-hdr{display:flex;align-items:center;justify-content:space-between;padding:20px 24px 16px;border-bottom:1px solid var(--body-color-less2);position:sticky;top:0;background:var(--body-color-less);z-index:1}
        .rd-modal-title{font-size:17px;font-weight:700;letter-spacing:-.3px}
        .rd-modal-close{width:30px;height:30px;border-radius:7px;background:var(--body-color-less2);border:1px solid var(--body-color-less2);color:var(--text-color);opacity:.5;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:17px;transition:all .15s}
        .rd-modal-close:hover{opacity:1}
        .rd-modal-body{padding:22px 24px;flex:1}
        .rd-modal-footer{display:flex;justify-content:flex-end;gap:10px;padding:16px 24px;border-top:1px solid var(--body-color-less2);position:sticky;bottom:0;background:var(--body-color-less)}

        /* Form Section */
        .rd-fs{margin-bottom:20px}
        .rd-fs:last-child{margin-bottom:0}
        .rd-fs-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;opacity:.3;margin-bottom:14px;padding-bottom:10px;border-bottom:1px solid var(--body-color-less2)}

        /* Radio Cards */
        .rd-rc{border:1.5px solid var(--body-color-less2);border-radius:9px;padding:12px 14px;margin-bottom:10px;cursor:pointer;transition:border-color .2s,background .2s}
        .rd-rc:hover{border-color:rgba(99,102,241,.35);background:rgba(99,102,241,.025)}
        .rd-rc.selected{border-color:rgba(99,102,241,.45);background:rgba(99,102,241,.05)}
        .rd-rc-top{display:flex;align-items:center;gap:10px}
        .rd-rc input[type=radio]{accent-color:#6366f1;width:15px;height:15px;flex-shrink:0;cursor:pointer}
        .rd-rc-label{font-size:14px;font-weight:500}
        .rd-rc-url{margin-top:10px;padding-left:25px}

        /* Collapse */
        .rd-col-hdr{display:flex;align-items:center;justify-content:space-between;cursor:pointer;padding:12px 16px;background:var(--body-color);border:1px solid var(--body-color-less2);border-radius:9px;transition:border-color .2s}
        .rd-col-hdr:hover{border-color:rgba(99,102,241,.3)}
        .rd-col-hdr.open{border-radius:9px 9px 0 0;border-bottom-color:transparent}
        .rd-col-label{font-size:13px;font-weight:600;display:flex;align-items:center;gap:8px}
        .rd-col-chev{font-size:11px;opacity:.4;transition:transform .25s}
        .rd-col-hdr.open .rd-col-chev{transform:rotate(180deg)}
        .rd-col-body{background:var(--body-color);border:1px solid var(--body-color-less2);border-top:none;border-radius:0 0 9px 9px;max-height:0;overflow:hidden;transition:max-height .3s ease,padding .3s ease}
        .rd-col-body.open{max-height:400px;padding:16px}

        /* Check */
        .rd-chk{display:flex;align-items:center;gap:10px;cursor:pointer;font-size:14px;margin-bottom:10px}
        .rd-chk:last-child{margin-bottom:0}
        .rd-chk input[type=checkbox]{accent-color:#6366f1;width:15px;height:15px;cursor:pointer}
        .rd-code{font-size:12px;color:#818cf8;background:rgba(99,102,241,.1);padding:1px 7px;border-radius:4px;font-family:monospace}

        /* Example */
        .rd-ex-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;opacity:.3;margin-bottom:6px;margin-top:16px}
        .rd-ex-box{background:var(--body-color);border:1px solid var(--body-color-less2);border-radius:8px;padding:10px 14px;font-family:monospace;font-size:12px;color:#818cf8;word-break:break-all;line-height:1.6}

        /* Confirm */
        .rd-cfm-bg{position:fixed;inset:0;background:rgba(0,0,0,.6);backdrop-filter:blur(3px);z-index:10001;display:flex;align-items:center;justify-content:center;opacity:0;pointer-events:none;transition:opacity .2s}
        .rd-cfm-bg.open{opacity:1;pointer-events:all}
        .rd-cfm-box{background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:14px;padding:24px;width:340px;max-width:90vw;transform:translateY(12px);transition:transform .2s}
        .rd-cfm-bg.open .rd-cfm-box{transform:translateY(0)}
        .rd-cfm-title{font-size:16px;font-weight:700;margin-bottom:8px}
        .rd-cfm-body{font-size:13px;opacity:.5;margin-bottom:20px}
        .rd-cfm-btns{display:flex;justify-content:flex-end;gap:10px}

        /* Toast */
        .rd-toast{position:fixed;bottom:24px;right:24px;z-index:200;background:var(--body-color-less);border:1px solid #10b981;border-radius:10px;padding:12px 18px;font-size:13px;font-weight:500;box-shadow:0 8px 28px rgba(0,0,0,.3);transform:translateY(20px);opacity:0;pointer-events:none;transition:transform .3s cubic-bezier(.34,1.56,.64,1),opacity .25s}
        .rd-toast.show{transform:translateY(0);opacity:1}
        .rd-toast.error{border-color:#ef4444}

        /* Table min-width for horizontal scroll */
        .rd-tbl{min-width:580px}

        /* ── Mobile ── */
        @media(max-width:600px){
          .rd-page{padding:16px 12px 80px}
          .rd-hdr{gap:12px}
          .rd-title{font-size:18px}
          .rd-card{padding:16px 14px}
          .rd-card-title{flex-wrap:wrap;gap:6px}
          .rd-modal-hdr{padding:16px 16px 12px}
          .rd-modal-title{font-size:15px}
          .rd-modal-body{padding:16px}
          .rd-modal-footer{flex-wrap:wrap;gap:8px;padding:14px 16px;justify-content:stretch}
          .rd-modal-footer .rd-btn-cancel,
          .rd-modal-footer .rd-btn-save,
          .rd-modal-footer .rd-btn-savedef{flex:1 1 auto;text-align:center;justify-content:center;padding:0 12px;font-size:13px;min-width:0;white-space:nowrap}
          .rd-th{flex-wrap:wrap;gap:8px;padding:12px 14px}
          .rd-td-actions{flex-wrap:wrap;gap:6px}
          .rd-cfm-btns{flex-wrap:wrap}
          .rd-toast{left:12px;right:12px;bottom:16px}
          .rd-col-body.open{padding:12px}
          .rd-fs-label{font-size:9px}
          .rd-rc{padding:10px 12px}
          .rd-rc-url{padding-left:20px}
          .rd-empty{padding:32px 14px}
        }
      `}</style>

      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div className="rd-page">
            {/* ── Header ── */}
            <div className="rd-hdr">
              <div>
                <h1 className="rd-title">Redirects</h1>
                <p className="rd-sub">
                  Control where customers are sent after checkout
                </p>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                {!isReadOnly && (
                  <button
                    className="btn btn-primary"
                    onClick={() => openModal(null)}
                  >
                    <i className="mdi mdi-plus" style={{ fontSize: 15 }}></i>{" "}
                    Create Redirect Template
                  </button>
                )}
              </div>
            </div>

            {/* ── Default Card ── */}
            <div
              className={`rd-card ${
                isDefaultActive ? "rd-default-active" : ""
              }`}
            >
              <div className="rd-card-title">
                Default Redirects
                {isDefaultActive && (
                  <span className="rd-default-badge">
                    <i
                      className="mdi mdi-check-circle"
                      style={{ fontSize: 11 }}
                    ></i>{" "}
                    Active Default
                  </span>
                )}
              </div>
              <div className="rd-card-sub">
                Applied globally to all payments when no specific template is
                selected
              </div>

              <div className="rd-field">
                <label className="rd-label">
                  <i
                    className="mdi mdi-check-circle"
                    style={{ fontSize: 13, color: "#10b981" }}
                  ></i>{" "}
                  Success URL
                </label>
                <input
                  className="rd-input"
                  type="url"
                  placeholder="https://yourdomain.com/success"
                  value={defaultSuccess}
                  readOnly
                  style={{
                    opacity: defaultSuccess ? 1 : 0.5,
                    cursor: "default",
                  }}
                />
              </div>

              <div className="rd-field">
                <label className="rd-label">
                  <i
                    className="mdi mdi-close-circle"
                    style={{ fontSize: 13, color: "#ef4444" }}
                  ></i>{" "}
                  Failure URL
                </label>
                <input
                  className="rd-input"
                  type="url"
                  placeholder="https://yourdomain.com/failure"
                  value={defaultFailure}
                  readOnly
                  style={{
                    opacity: defaultFailure ? 1 : 0.5,
                    cursor: "default",
                  }}
                />
              </div>

              <div className="rd-field" style={{ marginBottom: 20 }}>
                <label className="rd-label">
                  <i
                    className="mdi mdi-information-outline"
                    style={{ fontSize: 13, opacity: 0.5 }}
                  ></i>{" "}
                  Cancel URL <span className="rd-opt">optional</span>
                </label>
                <input
                  className="rd-input"
                  type="url"
                  placeholder="https://yourdomain.com/cancel"
                  value={defaultCancel}
                  readOnly
                  style={{
                    opacity: defaultCancel ? 1 : 0.5,
                    cursor: "default",
                  }}
                />
              </div>

              {!isReadOnly && (
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <button className="rd-btn-success" onClick={makeDefaultCard}>
                    <i
                      className="mdi mdi-star-outline"
                      style={{ fontSize: 14 }}
                    ></i>{" "}
                    Make Default
                  </button>
                </div>
              )}
            </div>

            {/* ── Template Table ── */}
            <div className="rd-tw">
              <div className="rd-th">
                <span className="rd-th-title">Redirect Templates</span>
                <span className="rd-th-count">
                  {isLoading
                    ? "Loading…"
                    : `${templates.length} template${
                        templates.length !== 1 ? "s" : ""
                      }`}
                </span>
              </div>

              {isLoading ? (
                <div className="rd-empty">
                  <div className="rd-empty-icon">
                    <i className="mdi mdi-loading mdi-spin"></i>
                  </div>
                  <div className="rd-empty-t">Loading templates…</div>
                </div>
              ) : templates.length === 0 ? (
                <div className="rd-empty">
                  <div className="rd-empty-icon">
                    <i className="mdi mdi-redo-variant"></i>
                  </div>
                  <div className="rd-empty-t">No redirect templates yet</div>
                  <div className="rd-empty-s">
                    Create a template to control post-checkout redirects
                  </div>
                  {!isReadOnly && (
                    <button
                      className="rd-btn rd-btn-primary"
                      style={{ marginTop: 4 }}
                      onClick={() => openModal(null)}
                    >
                      Create Redirect Template
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="rd-tbl">
                    <thead>
                      <tr>
                        <th>Template Name</th>
                        <th>Success URL</th>
                        <th>Failure URL</th>
                        <th>Default</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map((t) => (
                        <tr key={t.id}>
                          <td className="rd-td-name">{t.name}</td>
                          <td
                            className="rd-td-url"
                            title={
                              t.successMode === "url"
                                ? t.success || "—"
                                : "Hosted page"
                            }
                          >
                            {t.successMode === "url" ? (
                              t.success || "—"
                            ) : (
                              <em style={{ opacity: 0.5 }}>Hosted page</em>
                            )}
                           </td>
                          <td
                            className="rd-td-url"
                            title={
                              t.failureMode === "url"
                                ? t.failure || "—"
                                : "Hosted page"
                            }
                          >
                            {t.failureMode === "url" ? (
                              t.failure || "—"
                            ) : (
                              <em style={{ opacity: 0.5 }}>Hosted page</em>
                            )}
                           </td>
                          <td>
                            {t.isDefault ? (
                              <span className="rd-default-badge">
                                <i
                                  className="mdi mdi-check-circle"
                                  style={{ fontSize: 10 }}
                                ></i>{" "}
                                Default
                              </span>
                            ) : (
                              <span style={{ opacity: 0.25 }}>—</span>
                            )}
                           </td>
                          <td>
                            <div className="rd-td-actions">
                              {isReadOnly ? (
                                <span style={{ opacity: 0.4, fontSize: 12 }}>
                                  —
                                </span>
                              ) : (
                                <>
                                  <button
                                    className="rd-btn-outline-ind"
                                    onClick={() => openModal(t.id)}
                                  >
                                    Edit
                                  </button>
                                  {!t.isDefault && (
                                    <button
                                      className="rd-btn-danger-sm"
                                      onClick={() => deleteTemplate(t.id)}
                                    >
                                      Delete
                                    </button>
                                  )}
                                  {!t.isDefault && (
                                    <button
                                      className="rd-btn-outline-ind"
                                      style={{
                                        borderColor: "rgba(16,185,129,.4)",
                                        color: "#10b981",
                                      }}
                                      onClick={() => setTemplateDefault(t.id)}
                                    >
                                      Set Default
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                           </td>
                        </tr>
                      ))}
                    </tbody>
                   </table>
                </div>
              )}
            </div>
          </div>
          <Footer />
        </div>
      </div>

      {/* ═══ CREATE/EDIT MODAL ═══ */}
      <div
        className={`rd-mov ${modalOpen ? "open" : ""}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) closeModal();
        }}
      >
        <div className="rd-modal">
          <div className="rd-modal-hdr">
            <span className="rd-modal-title">
              {editingId
                ? "Edit Redirect Template"
                : "Create Redirect Template"}
            </span>
            <button className="rd-modal-close" onClick={closeModal}>
              <i className="mdi mdi-close"></i>
            </button>
          </div>
          <div className="rd-modal-body">
            {/* Name */}
            <div className="rd-field" style={{ marginBottom: 20 }}>
              <label className="rd-label">
                Template Name <span className="rd-req">*</span>
              </label>
              <input
                className={`rd-input ${nameError ? "rd-input-error" : ""}`}
                value={tplName}
                onChange={(e) => {
                  setTplName(e.target.value);
                  setNameError("");
                }}
                placeholder="e.g. Campaign A Redirects"
              />
              {nameError && <span className="rd-error">{nameError}</span>}
            </div>

            {/* Success */}
            <div className="rd-fs">
              <div className="rd-fs-label">On Payment Success</div>
              <div
                className={`rd-rc ${successMode === "url" ? "selected" : ""}`}
                onClick={() => setSuccessMode("url")}
              >
                <div className="rd-rc-top">
                  <input
                    type="radio"
                    checked={successMode === "url"}
                    onChange={() => setSuccessMode("url")}
                  />
                  <span className="rd-rc-label">Redirect to URL</span>
                </div>
                {successMode === "url" && (
                  <div className="rd-rc-url">
                    <input
                      className={`rd-input ${
                        successUrlError ? "rd-input-error" : ""
                      }`}
                      type="url"
                      value={tplSuccess}
                      onChange={(e) => {
                        setTplSuccess(e.target.value);
                        setSuccessUrlError("");
                      }}
                      placeholder="https://yourdomain.com/success"
                    />
                    {successUrlError && (
                      <span className="rd-error">{successUrlError}</span>
                    )}
                  </div>
                )}
              </div>
              <div
                className={`rd-rc ${
                  successMode === "hosted" ? "selected" : ""
                }`}
                onClick={() => setSuccessMode("hosted")}
              >
                <div className="rd-rc-top">
                  <input
                    type="radio"
                    checked={successMode === "hosted"}
                    onChange={() => setSuccessMode("hosted")}
                  />
                  <span className="rd-rc-label">Show hosted success page</span>
                </div>
              </div>
            </div>

            {/* Failure */}
            <div className="rd-fs">
              <div className="rd-fs-label">On Payment Failure</div>
              <div
                className={`rd-rc ${failureMode === "url" ? "selected" : ""}`}
                onClick={() => setFailureMode("url")}
              >
                <div className="rd-rc-top">
                  <input
                    type="radio"
                    checked={failureMode === "url"}
                    onChange={() => setFailureMode("url")}
                  />
                  <span className="rd-rc-label">Redirect to URL</span>
                </div>
                {failureMode === "url" && (
                  <div className="rd-rc-url">
                    <input
                      className={`rd-input ${
                        failureUrlError ? "rd-input-error" : ""
                      }`}
                      type="url"
                      value={tplFailure}
                      onChange={(e) => {
                        setTplFailure(e.target.value);
                        setFailureUrlError("");
                      }}
                      placeholder="https://yourdomain.com/failure"
                    />
                    {failureUrlError && (
                      <span className="rd-error">{failureUrlError}</span>
                    )}
                  </div>
                )}
              </div>
              <div
                className={`rd-rc ${
                  failureMode === "hosted" ? "selected" : ""
                }`}
                onClick={() => setFailureMode("hosted")}
              >
                <div className="rd-rc-top">
                  <input
                    type="radio"
                    checked={failureMode === "hosted"}
                    onChange={() => setFailureMode("hosted")}
                  />
                  <span className="rd-rc-label">Show hosted failure page</span>
                </div>
              </div>
            </div>

            {/* Advanced */}
            <div className="rd-fs" style={{ marginBottom: 0 }}>
              <div
                className={`rd-col-hdr ${advOpen ? "open" : ""}`}
                onClick={() => setAdvOpen(!advOpen)}
              >
                <span className="rd-col-label">
                  <i className="mdi mdi-cogs" style={{ fontSize: 14 }}></i>{" "}
                  Advanced Options
                </span>
                <span className="rd-col-chev">
                  <i className="mdi mdi-chevron-down"></i>
                </span>
              </div>
              <div className={`rd-col-body ${advOpen ? "open" : ""}`}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    opacity: 0.45,
                    marginBottom: 12,
                  }}
                >
                  Append query parameters to redirect URLs
                </div>
                <div>
                  <label className="rd-chk">
                    <input
                      type="checkbox"
                      checked={qpPaymentId}
                      onChange={(e) => setQpPaymentId(e.target.checked)}
                    />
                    <span className="rd-code">payment_id</span>
                  </label>
                  <label className="rd-chk">
                    <input
                      type="checkbox"
                      checked={qpStatus}
                      onChange={(e) => setQpStatus(e.target.checked)}
                    />
                    <span className="rd-code">status</span>
                  </label>
                  <label className="rd-chk">
                    <input
                      type="checkbox"
                      checked={qpEmail}
                      onChange={(e) => setQpEmail(e.target.checked)}
                    />
                    <span className="rd-code">customer_email</span>
                  </label>
                  <label className="rd-chk">
                    <input
                      type="checkbox"
                      checked={qpAmount}
                      onChange={(e) => setQpAmount(e.target.checked)}
                    />
                    <span className="rd-code">amount</span>
                  </label>
                </div>
                <div className="rd-ex-label">Example output</div>
                <div className="rd-ex-box">{exampleOutput}</div>
              </div>
            </div>
          </div>
          <div className="rd-modal-footer">
            <button
              className="rd-btn-cancel"
              onClick={closeModal}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              className="rd-btn-save"
              onClick={() => saveTemplate(false)}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Template"}
            </button>
            <button
              className="rd-btn-savedef"
              onClick={() => saveTemplate(true)}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save & Make Default"}
            </button>
          </div>
        </div>
      </div>

      {/* ═══ CONFIRM ═══ */}
      <div
        className={`rd-cfm-bg ${confirmOpen ? "open" : ""}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) closeConfirm();
        }}
      >
        <div className="rd-cfm-box">
          <div className="rd-cfm-title">{confirmTitle}</div>
          <div className="rd-cfm-body">{confirmBody}</div>
          <div className="rd-cfm-btns">
            <button
              className="rd-btn-cancel"
              style={{ height: 36, fontSize: 13 }}
              onClick={closeConfirm}
            >
              Cancel
            </button>
            <button
              className={
                confirmBtnDanger ? "rd-btn-danger-sm" : "rd-btn-primary"
              }
              style={{
                height: 36,
                padding: "0 16px",
                fontSize: 13,
                borderRadius: 8,
              }}
              onClick={() => {
                closeConfirm();
                confirmCb.current && confirmCb.current();
              }}
            >
              {confirmBtnLabel}
            </button>
          </div>
        </div>
      </div>

      {/* ═══ TOAST ═══ */}
      <div
        className={`rd-toast ${toastShow ? "show" : ""} ${
          toastType === "error" ? "error" : ""
        }`}
      >
        {toastMsg}
      </div>
    </Fragment>
  );
};

export default Redirects;